import React from 'react';

import { useNavLinkGroup } from '../components/NavLinkGroup';
import { Context } from '../routers/router-context';
import type { Route } from '../types';
import { matchNavLink } from '../utils/matchNavLink';
import { useLinkProps, type UseLinkOptions, type UseLinkProps } from './useLinkProps';
import { useLocation } from './useLocation';

/** The options of `useLinkProps`, plus the id of the link inside its navigation group. */
export interface UseNavLinkOptions extends Omit<UseLinkOptions, 'route'> {
    /** Route the link points to. */
    route?: Route;
    /**
     * Id of the link inside its navigation group. One is generated when it is absent.
     * Pass one when another registry must name the same link.
     */
    id?: string;
}

export interface UseNavLink {
    /** Id of the link inside its navigation group. */
    id: string;
    /** Whether this link points at the page in view. */
    isCurrentPage: boolean;
    /** Props of the anchor, from `useLinkProps`. */
    linkProps: UseLinkProps;
}

/**
 * Wire one navigation link to the router: where it goes, and whether it points at the page in
 * view.
 *
 * The link matches its own route against the page. Inside a `NavLinkGroup`, a link that matches
 * claims the page with its matched URL length, and the group picks the longest claim, so exactly
 * one link of the group is current. Outside any group, the match alone is the answer, which is
 * react-router's own `NavLink` behaviour.
 */
export const useNavLink = ({ route, id: idProp, ...linkOptions }: UseNavLinkOptions): UseNavLink => {
    const group = useNavLinkGroup();
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const { pathname } = useLocation();
    const context = React.useContext(Context);

    const match = matchNavLink(route, { pathname, context });
    const matchedUrlLength = match?.matchedUrlLength ?? -1;
    const isLegacyMatch = match?.isLegacyMatch ?? false;
    const isMatch = match !== null;

    /**
     * Claim the page while this link matches it. A link that matches nothing writes nothing. A
     * navigation changes the matched length, so the old claim is disposed and a new one is made, in
     * the same layout phase.
     *
     * Layout effect on purpose: the group must see every claim before paint.
     */
    React.useLayoutEffect(() => {
        if (!group || !isMatch) {
            return undefined;
        }

        return group.claim({ id, matchedUrlLength, isLegacyMatch });
    }, [group, id, isMatch, matchedUrlLength, isLegacyMatch]);

    const linkProps = useLinkProps({ route, ...linkOptions });

    return { id, isCurrentPage: group ? group.winnerId === id : isMatch, linkProps };
};
